import { createFileRoute } from "@tanstack/react-router";

import { PageHero } from "@/components/site/PageHero";
import { SiteFooter } from "@/components/site/SiteFooter";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — adaptED" },
      {
        name: "description",
        content:
          "Learn how adaptED collects, uses and protects your personal information.",
      },
      { property: "og:title", content: "Privacy Policy — adaptED" },
      {
        property: "og:description",
        content:
          "How adaptED collects, uses and protects personal information.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/privacy" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "Privacy Policy — adaptED" },
      {
        name: "twitter:description",
        content:
          "How adaptED collects, uses and protects personal information.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.adapted.info/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main id="top">
      <PageHero
        label="Legal"
        title="Privacy Policy"
        paragraph="How we collect, use and safeguard your information at adaptED."
      />

      <section className="surface-warm py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="prose prose-lg max-w-none text-warm-ink">
            <p className="text-sm text-warm-ink/60">Last updated: 15 August 2026</p>

            <h2>1. Introduction</h2>
            <p>
              adaptED ("we", "us" or "our") is committed to protecting your privacy. This Privacy
              Policy explains how we handle personal information when you use our website, learning
              companion and related services.
            </p>

            <h2>2. Information we collect</h2>
            <p>We may collect the following types of information:</p>
            <ul>
              <li>
                <strong>Account information:</strong> name, email address, school name, grade and
                contact details you provide when registering or contacting us.
              </li>
              <li>
                <strong>Usage information:</strong> how you interact with the platform, including
                learning activity, preferences and device information.
              </li>
              <li>
                <strong>Communications:</strong> messages you send us through forms, email or support
                channels.
              </li>
            </ul>

            <h2>3. How we use your information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, personalise and improve the learning experience;</li>
              <li>Communicate with you about your account, updates and support requests;</li>
              <li>Understand usage patterns and improve our products;</li>
              <li>Maintain the security and integrity of our Services; and</li>
              <li>Comply with legal obligations.</li>
            </ul>

            <h2>4. Sharing your information</h2>
            <p>
              We do not sell personal information. We only share data with trusted service providers
              who help us operate the platform, and only where necessary. We may also disclose
              information if required by law or to protect our rights.
            </p>

            <h2>5. Data security</h2>
            <p>
              We implement appropriate technical and organisational measures to protect your
              information. No online service can guarantee complete security, but we work hard to keep
              your data safe.
            </p>

            <h2>6. Your rights</h2>
            <p>
              Depending on your location, you may have the right to access, correct, delete or
              restrict the use of your personal information. To exercise these rights, please contact
              us through our{" "}
              <a href="/contact" className="text-primary-strong hover:underline">
                Contact page
              </a>
              .
            </p>

            <h2>7. Cookies and similar technologies</h2>
            <p>
              We may use cookies and similar technologies to operate the website, remember your
              preferences and understand how the Services are used. You can manage cookie preferences
              through your browser settings.
            </p>

            <h2>8. Children's privacy</h2>
            <p>
              Our Services are designed for high school learners. We encourage parents and guardians
              to review their children's online activity. We do not knowingly collect more
              information than is reasonably necessary to provide the educational Services.
            </p>

            <h2>9. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The revised version will be posted
              on this page with an updated date.
            </p>

            <h2>10. Contact us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please reach
              out through our{" "}
              <a href="/contact" className="text-primary-strong hover:underline">
                Contact page
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
